import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import PlayIcon from "@mui/icons-material/PlayArrowOutlined";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  hlsSiblingUrl,
  nativeHlsSupported,
  streamPreviewVerdict,
} from "@shared/stream-preview";
import type { MediaResourceDraft } from "@shared/asset-draft";
import { tokens } from "../../theme";

/**
 * Watch the stream, in the editor, before promising a guest it plays.
 *
 * ## Why this is a player and not a link
 *
 * The stream URLs on a row are typed, pasted and ingested by four different
 * routes, and until now the only way to find out whether one resolved to actual
 * video was to open a terminal. Everything else in this dialog is a DERIVED
 * verdict over metadata - `assetPlaybackBlockers` can prove the row is
 * *shaped* like something playable and cannot prove a single byte exists.
 *
 * ## Loaded on demand, and that is deliberate
 *
 * `hls.js` is imported dynamically inside the play handler, so the CMS bundle
 * that every operator downloads to edit a premise does not carry a video player
 * for a dialog most of them never open.
 *
 * ## Two settings that look like tuning and are not
 *
 * - **`enableWorker: false`.** hls.js spawns its demuxer worker from a `blob:`
 *   URL. Foyer's CSP has no `worker-src`, so it falls back to `default-src
 *   'self'` and the worker is blocked. hls.js survives that (it catches the
 *   failure and runs inline) but it does so by way of an exception on every
 *   load, which is indistinguishable in the console from the real errors this
 *   component exists to surface. Off by choice, on a preview where throughput
 *   does not matter.
 * - **`crossOrigin` is left unset.** Setting it makes the browser send a CORS
 *   preflight for the segments, and a CDN that serves the estate's players
 *   perfectly well may not answer one - which would turn a healthy stream into a
 *   preview failure that exists only in the CMS.
 *
 * ⚠️ **A green preview here is not a green light for a television.** See the
 * `panel-video-encoding` note: High@L4.2 at 12 Mbps plays in this box and
 * stalls on the panel after five seconds. The caption under the player says so,
 * because the alternative is somebody quoting a CMS preview in a support thread.
 */

export default function StreamPreview({
  resource,
  siblings,
}: {
  resource: MediaResourceDraft;
  /** The other resources on the same media, for a better refusal. */
  siblings: readonly MediaResourceDraft[];
}) {
  const { t } = useTranslation("content");
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const detachRef = useRef<(() => void) | null>(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Probing needs a real element, and there is none before the first render, so
  // the verdict is computed once the player is open and assumed MSE-only until
  // then. Getting this backwards would claim "native" on a Chrome that has no
  // native HLS at all.
  const [hasNativeHls, setHasNativeHls] = useState(false);
  const verdict = streamPreviewVerdict(resource, hasNativeHls);

  // Tearing down matters more than it looks: an HLS instance left attached
  // keeps fetching segments off a CDN for a dialog nobody is looking at.
  useEffect(() => {
    return () => {
      detachRef.current?.();
      detachRef.current = null;
    };
  }, []);

  // A row edited under an open player (URL retyped, Encrypted ticked) must not
  // keep playing whatever was loaded before the edit - that is the one state
  // where the screen and the form disagree about what the row IS.
  useEffect(() => {
    detachRef.current?.();
    detachRef.current = null;
    setOpen(false);
    setError(null);
  }, [resource.url, resource.protocol, resource.isEncrypted]);

  async function play() {
    setError(null);
    setOpen(true);
    setLoading(true);
    // One frame, so the <video> the player attaches to exists.
    await new Promise((resolve) => setTimeout(resolve, 0));
    const video = videoRef.current;
    if (!video) {
      setLoading(false);
      return;
    }

    const native = nativeHlsSupported(video);
    setHasNativeHls(native);
    const decision = streamPreviewVerdict(resource, native);
    if (!decision.playable) {
      setLoading(false);
      return;
    }

    try {
      if (decision.via === "native") {
        video.src = resource.url;
        detachRef.current = () => {
          video.removeAttribute("src");
          video.load();
        };
      } else {
        const { default: Hls } = await import("hls.js");
        if (!Hls.isSupported()) {
          setError(t("streamPreview.noMse"));
          setLoading(false);
          return;
        }
        const hls = new Hls({ enableWorker: false });
        hls.on(Hls.Events.ERROR, (_event, data) => {
          // Only fatal errors are reported. hls.js recovers from a great many
          // non-fatal ones by itself, and surfacing those would train an
          // operator to ignore this box.
          if (!data.fatal) return;
          setError(
            data.response?.code
              ? t("streamPreview.hostAnswered", {
                  code: data.response.code,
                  details: data.details,
                })
              : t("streamPreview.playbackFailed", { details: data.details })
          );
          setLoading(false);
        });
        hls.loadSource(resource.url);
        hls.attachMedia(video);
        detachRef.current = () => hls.destroy();
      }
      await video.play().catch(() => {
        // Autoplay refusal is not a stream failure - the controls are there and
        // the operator can press play. Saying "playback failed" here would be a
        // lie about the URL.
      });
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("streamPreview.couldNotOpen")
      );
    } finally {
      setLoading(false);
    }
  }

  const sibling = !verdict.playable ? hlsSiblingUrl(siblings) : null;

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Button
        size="small"
        startIcon={<PlayIcon />}
        onClick={() => void play()}
        disabled={loading}
        data-testid="stream-preview-play"
        sx={{ alignSelf: "flex-start", minHeight: 0 }}
      >
        {open ? t("streamPreview.reload") : t("streamPreview.preview")}
      </Button>

      {open && !verdict.playable && (
        <Alert
          severity={verdict.reason === "encrypted" ? "info" : "warning"}
          data-testid={`stream-preview-refused-${verdict.reason}`}
          sx={{ typography: "meta" }}
        >
          {t(`streamPreview.refusal.${verdict.reason}`)}
          {verdict.reason === "no_dash_player" && sibling
            ? ` ${t("streamPreview.hlsSibling")}`
            : ""}
        </Alert>
      )}

      {open && verdict.playable && (
        <Box>
          <Box
            component="video"
            ref={videoRef}
            controls
            playsInline
            muted
            data-testid="stream-preview-video"
            sx={{
              width: "100%",
              maxWidth: 520,
              aspectRatio: "16 / 9",
              bgcolor: "#000",
              borderRadius: 1.5,
              border: `1px solid ${tokens.divider}`,
              display: "block",
            }}
          />
          {error && (
            <Alert
              severity="error"
              sx={{ mt: 1, typography: "meta" }}
              data-testid="stream-preview-error"
            >
              {error}
            </Alert>
          )}
          <Typography variant="micro" sx={{ color: tokens.inkMuted, mt: 0.75 }}>
            {t("streamPreview.caption")}
          </Typography>
        </Box>
      )}
    </Stack>
  );
}
